import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import { StopIcon } from '@heroicons/react/24/solid'
import { MicrophoneIcon } from '@heroicons/react/24/outline'

import { AssistantUiKey } from '../../../../pure-js/libs/assistantMessages'
import { INTERAX_COLORS } from '../../constants/colors'
import { TESTIDS } from '../../config/testids'
import { useTranslation } from '../hooks/useIntractionTranslate'

export interface AriaVoiceControlsProps {
  muted: boolean
  onStop: () => void
  onToggleMute: () => void
  /** The coach page can afford slightly larger targets than the floating panel. */
  size?: number
}

const DEFAULT_BUTTON_SIZE_PX = 38
/** Icon and mute badge scale with the button so both sizes stay balanced. */
const ICON_SIZE_RATIO = 0.47
const MUTED_BADGE_SIZE_RATIO = 0.42

export const AriaVoiceControls = ({
  muted,
  onStop,
  onToggleMute,
  size = DEFAULT_BUTTON_SIZE_PX
}: AriaVoiceControlsProps) => {
  const { t } = useTranslation()
  const muteLabel = muted ? t(AssistantUiKey.UNMUTE_MIC) : t(AssistantUiKey.MUTE_MIC)
  const iconSize = Math.round(size * ICON_SIZE_RATIO)
  const badgeSize = Math.round(size * MUTED_BADGE_SIZE_RATIO)
  const squareSx = { width: size, height: size }

  return (
    <Stack direction="row" gap={1.25} alignItems="center">
      <Tooltip title={muteLabel}>
        <IconButton
          aria-label={muteLabel}
          data-testid={TESTIDS.ASSISTANT_VOICE_MUTE_BTN}
          onClick={onToggleMute}
          size="small"
          sx={{
            ...squareSx,
            position: 'relative',
            color: muted ? INTERAX_COLORS.redError : INTERAX_COLORS.white,
            border: `1px solid ${muted ? INTERAX_COLORS.redErrorBackground : INTERAX_COLORS.white15}`,
            backgroundColor: muted ? INTERAX_COLORS.redErrorBackground : INTERAX_COLORS.white04
          }}
        >
          {/* The icon is the microphone in both states — it is the mic that mutes, not the speaker. */}
          <MicrophoneIcon width={iconSize} height={iconSize} />
          {muted && (
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: badgeSize,
                height: badgeSize,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                backgroundColor: INTERAX_COLORS.redError,
                color: INTERAX_COLORS.white,
                fontSize: badgeSize * 0.7,
                fontWeight: 700,
                lineHeight: 1
              }}
            >
              ×
            </Box>
          )}
        </IconButton>
      </Tooltip>

      <Tooltip title={t(AssistantUiKey.END_VOICE)}>
        <IconButton
          aria-label={t(AssistantUiKey.END_VOICE_ARIA_LABEL)}
          data-testid={TESTIDS.ASSISTANT_VOICE_END_BTN}
          onClick={onStop}
          size="small"
          sx={{
            ...squareSx,
            color: INTERAX_COLORS.redError,
            border: `1px solid ${INTERAX_COLORS.redErrorBackground}`,
            backgroundColor: INTERAX_COLORS.redErrorBackground
          }}
        >
          <StopIcon width={iconSize} height={iconSize} />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

export default AriaVoiceControls
